/* eslint-disable import/no-extraneous-dependencies */
import {
  Box,
  Button,
  Drawer,
  Skeleton,
  Typography,
  styled,
  useTheme,
  Dialog,
} from '@mui/material'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Campaign as CampaignIcon } from '@mui/icons-material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { SecondaryButton } from '$common/buttons'
import { BugIcon, FeaturesIcon, MovingIcon } from '$common/icons'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { CHAT_LIST_APP_BAR_HEIGHT } from '$common/app-bar/app-bar.constants'
import { ContentContainer } from './content-container'
import { useGetChangelog } from '$logic/client-device/react-hook'
import { timeCalculation } from '$helper/functions/time'
import { toLocale } from '$helper/functions/text'
import { t } from '$i18n'

const StyledDrawer = styled(Drawer)({
  '.MuiDrawer-paper': {
    maxHeight: `calc(100% - ${CHAT_LIST_APP_BAR_HEIGHT}px)`,
    height: `calc(100% - ${CHAT_LIST_APP_BAR_HEIGHT}px)`,
    borderRadius: '15px 15px 0 0',
    backgroundColor: 'white',
    overflow: 'hidden',
  },
})

const HeaderContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 20px 16px;
  border-radius: 16px 16px 0 0;
  width: 100%;
  height: 64px;
  background: ${({ theme }) => theme.palette.veryLightGray};
`

const DateContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const ScrollContainer = styled('div')({
  overflow: 'auto',
  display: 'flex',
  scrollbarColor: 'rgba(0, 0, 0, 0.5) transparent',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 6px #fff',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgb(204, 204, 204)',
    borderRadius: '20px',
  },
  '& > div': {
    width: '100%',
  },
})

const StyledDivider = styled('div')`
  width: 100%;
  height: 0;
  margin: 0 16px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.05);
`

const ButtonContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  width: 100%;
  height: 80px;
  background-color: ${({ theme }) => theme.palette.white};
  margin-top: auto;
`

const StyledDialog = styled(Dialog)({
  '.MuiDialog-paper': {
    maxHeight: '600px',
    borderRadius: '15px',
    backgroundColor: 'white',

    width: '480px',
    maxWidth: '480px',
  },
}) // TODO: merge with confirm-dialog desktop dialog wrapper

interface Props {
  isOpen: boolean
  onClose: () => unknown
  children: JSX.Element[] | JSX.Element
  className?: string
}

function ChangeLogWrapper({ className, onClose, children, isOpen }: Props) {
  const isDesktop = useIsDesktopSize()

  return isDesktop ? (
    <StyledDialog open={isOpen} onClose={onClose} className={className}>
      {children}
    </StyledDialog>
  ) : (
    <StyledDrawer
      open={isOpen}
      anchor="bottom"
      onClose={() => {
        onClose()
        closeCurrentModal()
      }}
    >
      {children}
    </StyledDrawer>
  )
}

export function ChangelogModal({ isOpen }: { isOpen: boolean }) {
  const { items, loadMore, hasFinished, onClose, loading } = useGetChangelog()
  const theme = useTheme()

  if (loading || !items || !items.length) return null
  return (
    <ChangeLogWrapper
      isOpen={isOpen}
      onClose={() => {
        onClose()
        closeCurrentModal()
      }}
    >
      <HeaderContainer>
        <CampaignIcon sx={{ color: 'gray' }} />

        <Typography variant="subtitle1" mr={1.25}>
          {t('changelogModal.title')}
        </Typography>
      </HeaderContainer>

      <ScrollContainer>
        <InfiniteScroll
          height="100%"
          dataLength={items.length}
          next={loadMore}
          hasMore={!hasFinished}
          loader={<Skeleton variant="rectangular" width="100%" height="60px" />}
        >
          {items.map((item, index) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={index} padding="16px">
                <DateContainer>
                  <Typography variant="subtitle3" width="188px" color="gray">
                    {timeCalculation(item.date)}
                  </Typography>

                  <StyledDivider />

                  <Typography
                    width="188px"
                    textAlign="left"
                    variant="subtitle3"
                    color="gray"
                  >
                    {t('changelogModal.version')} {toLocale(item.version)}
                  </Typography>
                </DateContainer>

                {Boolean(item?.features?.length) && (
                  <ContentContainer
                    title={t('changelogModal.features')}
                    bgcolor={theme.palette.success.light}
                    icon={<FeaturesIcon />}
                    items={item.features}
                    mt={2}
                    mb={1.5}
                  />
                )}

                {Boolean(item?.improvements?.length) && (
                  <ContentContainer
                    title={t('changelogModal.improvements')}
                    bgcolor="#FFEBCC"
                    icon={<MovingIcon />}
                    items={item.improvements}
                    mt={3}
                    mb={1.5}
                  />
                )}

                {Boolean(item?.bugs?.length) && (
                  <ContentContainer
                    title={t('changelogModal.bugs')}
                    bgcolor={theme.palette.error.light}
                    icon={<BugIcon />}
                    items={item.bugs}
                    mt={3}
                    mb={1.5}
                  />
                )}
              </Box>
            )
          })}
        </InfiniteScroll>
      </ScrollContainer>

      <ButtonContainer>
        <SecondaryButton
          fullWidth
          text={t('common.understand')}
          onClick={async () => {
            await onClose()
            closeCurrentModal()
          }}
        />
      </ButtonContainer>
    </ChangeLogWrapper>
  )
}
